import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowDownToLine,
  BadgeCheck,
  CalendarDays,
  Check,
  ChevronRight,
  ClipboardList,
  Crown,
  Dumbbell,
  Download,
  Gauge,
  Gift,
  Headphones,
  Home,
  Lock,
  LogOut,
  Medal,
  Minus,
  Plus,
  Refrigerator,
  RotateCcw,
  Save,
  ShieldCheck,
  Sparkles,
  Speaker,
  Ticket,
  Trophy,
  Tv,
  Upload,
  User,
  Users,
  Volleyball,
  Watch
} from "lucide-react";
import { matches as seedMatches, stageLabels, teams as seedTeams } from "./data/worldCup2026.js";
import { store, usingDemoData } from "./services/store.js";
import { downloadCsv, parseBulkUsers, toCsv } from "./utils/csv.js";
import { rankUsers } from "./utils/ranking.js";

const navItems = [
  { key: "dashboard", label: "Inicio", icon: Home },
  { key: "matches", label: "Partidos", icon: CalendarDays },
  { key: "ranking", label: "Ranking", icon: Trophy },
  { key: "profile", label: "Perfil", icon: User }
];

const participantTypes = [
  { key: "medico", label: "Médico", plural: "Médicos" },
  { key: "colaborador", label: "Colaborador", plural: "Colaboradores" }
];

const rankingPrizes = [
  { from: 1, to: 1, place: "1er lugar", description: 'Televisor Hisense 50"', quantity: 1, icon: Tv },
  { from: 2, to: 2, place: "2do lugar", description: "Frigobar FRIGIDAIRE 4.3 pies", quantity: 1, icon: Refrigerator },
  { from: 3, to: 3, place: "3er lugar", description: "Bocina Bluetooth JBL", quantity: 1, icon: Speaker },
  { from: 4, to: 7, place: "4 al 7 lugar", description: "Smartwatch Xiaomi", quantity: 4, icon: Watch },
  { from: 8, to: 11, place: "8 al 11 lugar", description: "Certificado de regalo Soccer Mania", quantity: 4, icon: Ticket },
  { from: 12, to: 12, place: "12 lugar", description: "Pistola Masajeador Calor / Frío", quantity: 1, icon: Dumbbell },
  { from: 13, to: 14, place: "13 y 14 lugar", description: "Audífonos Inalámbricos Bluetooth AIWA", quantity: 2, icon: Headphones },
  { from: 15, to: 24, place: "15 al 24 lugar", description: "Balón de Fútbol", quantity: 10, icon: Volleyball },
  { from: 25, to: 34, place: "25 al 34 lugar", description: "Certificado de regalo Walmart", quantity: 10, icon: Gift },
  { from: 35, to: 54, place: "35 al 54 lugar", description: "Certificado de regalo Pollo Campero", quantity: 20, icon: Gift }
];

const podiumLabels = ["Primer lugar", "Segundo lugar", "Tercer lugar"];

const teamFlagCodes = {
  MEX: "mx",
  RSA: "za",
  KOR: "kr",
  CZE: "cz",
  CAN: "ca",
  BIH: "ba",
  QAT: "qa",
  SUI: "ch",
  BRA: "br",
  MAR: "ma",
  HAI: "ht",
  SCO: "gb-sct",
  USA: "us",
  PAR: "py",
  AUS: "au",
  TUR: "tr",
  GER: "de",
  CUW: "cw",
  CIV: "ci",
  ECU: "ec",
  NED: "nl",
  JPN: "jp",
  SWE: "se",
  TUN: "tn",
  BEL: "be",
  EGY: "eg",
  IRN: "ir",
  NZL: "nz",
  ESP: "es",
  CPV: "cv",
  KSA: "sa",
  URU: "uy",
  FRA: "fr",
  SEN: "sn",
  IRQ: "iq",
  NOR: "no",
  ARG: "ar",
  ALG: "dz",
  AUT: "at",
  JOR: "jo",
  POR: "pt",
  COD: "cd",
  UZB: "uz",
  COL: "co",
  ENG: "gb-eng",
  CRO: "hr",
  GHA: "gh",
  PAN: "pa"
};

function participantLabel(type, plural = false) {
  const found = participantTypes.find((item) => item.key === type);
  return plural ? found?.plural || "Colaboradores" : found?.label || "Colaborador";
}

const emptyWorkspace = {
  profile: null,
  currentUserRank: null,
  teams: [],
  matches: [],
  predictions: [],
  ranking: [],
  chatMessages: [],
  authorizedEmails: []
};

const APP_TIME_ZONE = "America/Guatemala";
const GUATEMALA_UTC_OFFSET = "-06:00";
const seedTeamsByCode = new Map(seedTeams.map((team) => [team.code, team]));

function assetUrl(path) {
  const cleanPath = path.replace(/^\/+/, "");
  if (import.meta.env.DEV) return `/${cleanPath}`;
  return new URL(cleanPath, import.meta.url).href;
}

const pulsoLogoDark = assetUrl("quiniela-centro-medico-logo.png");

function formatDate(dateLike, includeTime = true) {
  if (!dateLike) return "Fecha por definir";
  const date = new Date(dateLike);
  return new Intl.DateTimeFormat("es-GT", {
    timeZone: APP_TIME_ZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {})
  }).format(date);
}

function sortByKickoff(a, b) {
  return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime() || a.matchNumber - b.matchNumber;
}

function withDisplayOrder(matches) {
  return [...matches].sort(sortByKickoff).map((match, index) => ({
    ...match,
    displayOrder: index + 1
  }));
}

function getDisplayOrder(match) {
  return match.displayOrder ?? match.matchNumber;
}

function getPrizeWinners(ranking, prize) {
  return ranking.slice(prize.from - 1, prize.to).filter(Boolean);
}

function toDateTimeLocalValue(dateLike) {
  if (!dateLike) return "";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  })
    .formatToParts(date)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function dateTimeLocalValueToDate(value) {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/);
  const date = match ? new Date(`${match[1]}T${match[2]}:00${GUATEMALA_UTC_OFFSET}`) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getTeam(teams, code, placeholder = "Por definir") {
  if (code === "TBD") return { code, name: placeholder, flag: "◇" };
  const storedTeam = teams.find((team) => team.code === code);
  const seedTeam = seedTeamsByCode.get(code);

  if (seedTeam) {
    const merged = { ...seedTeam, ...storedTeam };
    if (!storedTeam?.name || storedTeam.name === code) merged.name = seedTeam.name;
    if (!storedTeam?.flag || storedTeam.flag === "◇") merged.flag = seedTeam.flag;
    return merged;
  }

  return storedTeam || { code, name: code, flag: "◇" };
}

function flagUrl(team) {
  const code = teamFlagCodes[team?.code];
  return code ? `https://flagcdn.com/${code}.svg` : null;
}

function TeamFlag({ team }) {
  const src = flagUrl(team);
  if (!src) return <span className="qc-team-flag qc-team-flag--empty">◇</span>;

  return <img alt={`Bandera de ${team.name}`} className="qc-team-flag" loading="lazy" src={src} />;
}

function getPrediction(predictions, matchId) {
  return predictions.find((prediction) => prediction.matchId === matchId);
}

function getMatchState(match, prediction) {
  if (match.status === "finished") return "Finalizado";
  if (new Date(match.lockAt).getTime() <= Date.now() || match.status === "closed") return "Cerrado";
  if (prediction) return "Pronosticado";
  return "Abierto";
}

function hasDefinedTeams(match) {
  return match.homeTeam !== "TBD" && match.awayTeam !== "TBD";
}

function isKnockoutMatch(match) {
  return match?.stage && match.stage !== "group";
}

function getResolvedWinnerTeam(match) {
  if (!match?.result || !isKnockoutMatch(match)) return null;
  if (match.winnerTeam) return match.winnerTeam;
  if (match.result.home > match.result.away) return match.homeTeam;
  if (match.result.away > match.result.home) return match.awayTeam;
  return null;
}

function formatTiebreaker(match, teams) {
  const winnerTeam = getResolvedWinnerTeam(match);
  if (!match?.tiebreaker || !winnerTeam) return "";
  const winner = getTeam(teams, winnerTeam);
  if (match.tiebreaker.type === "penalties") {
    return `Penales ${match.tiebreaker.home}-${match.tiebreaker.away}. Avanza ${winner.name}.`;
  }
  return `Avanza ${winner.name}.`;
}

function isPredictableMatch(match) {
  return (
    hasDefinedTeams(match) &&
    match.status !== "finished" &&
    match.status !== "closed" &&
    new Date(match.lockAt).getTime() > Date.now()
  );
}

function buildCsvExports(workspace) {
  const rankingCsv = toCsv(workspace.ranking, [
    { key: "name", label: "Nombre" },
    { key: "email", label: "Email" },
    { key: "totalPoints", label: "Puntos" },
    { key: "correctOutcomes", label: "Aciertos" },
    { key: "exactScores", label: "Marcadores exactos" },
    { key: "predictionsCount", label: "Predicciones" },
    { key: "participantType", label: "Tipo" },
    { key: "professionalLicense", label: "Número de colegiado" },
    { key: "employeeCode", label: "Código de colaborador" }
  ]);

  const authorizedCsv = toCsv(workspace.authorizedEmails, [
    { key: "name", label: "Nombre" },
    { key: "email", label: "Email" },
    { key: "participantType", label: "Tipo" },
    { key: "professionalLicense", label: "Número de colegiado" },
    { key: "employeeCode", label: "Código de colaborador" },
    { key: "used", label: "Cuenta activa" },
    { key: "importedAt", label: "Importado" },
    { key: "activatedAt", label: "Activado" }
  ]);

  return { rankingCsv, authorizedCsv };
}

function LoginScreen({ onLogin, onRegister, onPasswordReset, busy, error }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [participantType, setParticipantType] = useState("medico");
  const [professionalLicense, setProfessionalLicense] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setResetMessage("");
    setResetError("");
  };

  const submit = (event) => {
    event.preventDefault();
    setResetMessage("");
    setResetError("");
    if (mode === "login") {
      onLogin(email, password);
      return;
    }

    onRegister({
      name,
      email,
      password,
      participantType,
      professionalLicense,
      employeeCode
    });
  };

  const resetErrorMessage = (resetError) => {
    if (resetError.code === "auth/invalid-email") return "Escribe un correo electrónico válido.";
    if (resetError.code === "auth/user-not-found") return "No encontramos una cuenta con ese correo.";
    return "No pudimos enviar el correo de recuperación. Intenta de nuevo.";
  };

  const resetPassword = async () => {
    setResetMessage("");
    setResetError("");

    if (!email.trim()) {
      setResetError("Escribe tu correo electrónico para enviarte las instrucciones.");
      return;
    }

    setResetBusy(true);
    try {
      await onPasswordReset(email);
      setResetMessage("Te enviamos un correo con las instrucciones para restablecer tu contraseña.");
    } catch (resetError) {
      setResetError(resetErrorMessage(resetError));
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <main className="qc-login">
      <section className="qc-login__hero" aria-label="Bienvenida">
        <div className="qc-login__topline">
          <Logo />
          <span>Centro Médico</span>
        </div>
        <div className="qc-login__logo-stage">
          <img alt="Pulso Mundialista" src={pulsoLogoDark} />
        </div>
        <div className="qc-login__copy">
          <p className="qc-eyebrow">Pulso Mundialista</p>
          <h1>
            El Mundial <em>late</em> aquí.
          </h1>
          <p>
            Predice cada marcador, mira cómo se mueve el ranking y compite con tus compañeros durante todo el torneo.
          </p>
        </div>
      </section>

      <section className="qc-login__panel" aria-label="Acceso">
        <div className="qc-login__panel-header">
          <p className="qc-eyebrow">{mode === "login" ? "Acceso interno" : "Crear usuario"}</p>
          <h2>{mode === "login" ? "Entra a tu pulso." : "Únete a la quiniela."}</h2>
        </div>
        <div className="qc-auth-switch" role="tablist" aria-label="Modo de acceso">
          <button className={mode === "login" ? "is-active" : ""} onClick={() => switchMode("login")} type="button">
            Iniciar sesión
          </button>
          <button className={mode === "register" ? "is-active" : ""} onClick={() => switchMode("register")} type="button">
            Crear usuario
          </button>
        </div>
        <form onSubmit={submit} className="qc-form">
          {mode === "register" ? (
            <label>
              Nombre completo
              <input
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
                placeholder="Nombre y apellido"
                required
                type="text"
                value={name}
              />
            </label>
          ) : null}
          <label>
            Correo electrónico
            <input
              autoComplete="email"
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Escribe tu correo electrónico"
              required
              type="email"
              value={email}
            />
          </label>
          <label>
            Password
            <input
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              minLength={6}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Mínimo 6 caracteres"
              required
              type="password"
              value={password}
            />
          </label>
          {mode === "login" ? (
            <button className="qc-link-button" disabled={busy || resetBusy} onClick={resetPassword} type="button">
              {resetBusy ? "Enviando instrucciones..." : "¿Olvidaste tu password?"}
            </button>
          ) : null}
          {mode === "register" ? (
            <>
              <fieldset className="qc-role-picker">
                <legend>Médico o colaborador</legend>
                <div>
                  {participantTypes.map((type) => (
                    <button
                      className={participantType === type.key ? "is-active" : ""}
                      key={type.key}
                      onClick={() => setParticipantType(type.key)}
                      type="button"
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </fieldset>
              {participantType === "medico" ? (
                <label>
                  Número de colegiado
                  <input
                    autoComplete="off"
                    onChange={(event) => setProfessionalLicense(event.target.value)}
                    placeholder="Ej. 12345"
                    required
                    type="text"
                    value={professionalLicense}
                  />
                </label>
              ) : (
                <label>
                  Código de colaborador
                  <input
                    autoComplete="off"
                    onChange={(event) => setEmployeeCode(event.target.value)}
                    placeholder="Ej. CM-001"
                    required
                    type="text"
                    value={employeeCode}
                  />
                </label>
              )}
            </>
          ) : null}
          {error ? <p className="qc-error">{error}</p> : null}
          {resetError ? <p className="qc-error">{resetError}</p> : null}
          {resetMessage ? <p className="qc-success">{resetMessage}</p> : null}
          <button className="qc-button qc-button--primary" disabled={busy} type="submit">
            <ShieldCheck size={18} />
            {busy ? "Validando..." : mode === "login" ? "Iniciar sesión" : "Crear usuario"}
          </button>
        </form>
        <p className="qc-login__note">
          {mode === "login"
            ? "Usa tu cuenta registrada para guardar y editar tus pronósticos."
            : "Tu perfil quedará listo para participar en la tabla general."}
        </p>
      </section>
    </main>
  );
}

function Logo() {
  return (
    <div className="qc-logo" aria-label="Pulso Mundialista Centro Médico">
      <img alt="Pulso Mundialista" src={pulsoLogoDark} />
    </div>
  );
}

function AppShell({ children, currentView, profile, onNavigate, onLogout }) {
  const items = profile?.role === "admin" ? [...navItems, { key: "admin", label: "Admin", icon: Gauge }] : navItems;

  return (
    <div className={currentView === "admin" ? "qc-shell qc-shell--admin" : "qc-shell"}>
      <header className="qc-topbar">
        <Logo />
        <nav className="qc-nav" aria-label="Principal">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={currentView === item.key ? "is-active" : ""}
                key={item.key}
                onClick={() => onNavigate(item.key)}
                type="button"
              >
                <Icon size={17} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="qc-userchip">
          <span className="qc-userchip__points">{profile?.totalPoints ?? 0}</span>
          <span>pts</span>
          <button aria-label="Cerrar sesión" className="qc-icon-button" onClick={onLogout} type="button">
            <LogOut size={18} />
          </button>
        </div>
      </header>
      <main className="qc-main">{children}</main>
      <nav className="qc-bottomnav" aria-label="Principal móvil">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              className={currentView === item.key ? "is-active" : ""}
              key={item.key}
              onClick={() => onNavigate(item.key)}
              type="button"
            >
              <Icon size={20} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function DemoBanner({ onReset }) {
  if (!usingDemoData) return null;
  return (
    <div className="qc-demo-banner">
      <Sparkles size={16} />
      <span>Modo demo local activo. Agrega tus variables de Firebase para conectar datos reales.</span>
      <button onClick={onReset} type="button">
        <RotateCcw size={15} />
        Reiniciar demo
      </button>
    </div>
  );
}

function Dashboard({ workspace, onPredict }) {
  const { profile, currentUserRank, matches, predictions, ranking, teams } = workspace;
  const now = Date.now();
  const upcomingMatches = matches.filter(
    (match) => match.status !== "finished" && new Date(match.dateTime).getTime() >= now
  );
  const nextMatch = upcomingMatches.find((match) => new Date(match.lockAt).getTime() > now);
  const lastFinished = [...matches].reverse().find((match) => match.status === "finished" && match.result);
  const userRankIndex = ranking.findIndex((user) => user.uid === profile?.uid);
  const resolvedUserRank = currentUserRank ?? (userRankIndex >= 0 ? userRankIndex + 1 : null);
  const userRank = resolvedUserRank ? `#${resolvedUserRank}` : "...";
  const pending = matches.filter((match) => !getPrediction(predictions, match.id) && new Date(match.lockAt).getTime() > now).length;

  return (
    <div className="qc-page qc-fade">
      <section className="qc-dashboard-hero">
        <div className="qc-dashboard-hero__copy">
          <p className="qc-eyebrow">Pulso Mundialista · Hola, {profile?.name?.split(" ")[0] || "colaborador"}</p>
          <h1>
            Tu pulso <em>marca</em> el torneo.
          </h1>
          <p>Guarda tus marcadores, sigue tus aciertos y mantén el ritmo antes de cada cierre.</p>
        </div>
        <div className="qc-dashboard-hero__brand">
          <img alt="Pulso Mundialista" src={pulsoLogoDark} />
          <div className="qc-hero-score">
            <span>{profile?.totalPoints ?? 0}</span>
            <small>puntos</small>
          </div>
        </div>
      </section>

      <section className="qc-stat-grid" aria-label="Resumen">
        <StatCard icon={Trophy} label="Ranking" value={userRank} sub="Global" />
        <StatCard icon={BadgeCheck} label="Aciertos" value={profile?.correctOutcomes ?? 0} sub="Ganador o empate" />
        <StatCard icon={Medal} label="Exactos" value={profile?.exactScores ?? 0} sub="9 puntos base" />
        <StatCard icon={ClipboardList} label="Pendientes" value={pending} sub="Antes del cierre" />
      </section>

      <WinnersCelebration ranking={ranking} />

      {nextMatch ? (
        <section className="qc-next-match">
          <div>
            <p className="qc-eyebrow">Próximo partido</p>
            <MatchTeams match={nextMatch} teams={teams} />
            <p className="qc-muted">
              {formatDate(nextMatch.dateTime)} · {nextMatch.venue}
            </p>
          </div>
          <button className="qc-button qc-button--primary" onClick={() => onPredict(nextMatch.id)} type="button">
            Pronosticar
            <ChevronRight size={18} />
          </button>
        </section>
      ) : null}

      <div className="qc-two-col">
        <section>
          <SectionTitle title="Próximos partidos" />
          <div className="qc-list">
            {upcomingMatches.length ? (
              upcomingMatches.slice(0, 6).map((match) => (
                <MatchRow
                  key={match.id}
                  match={match}
                  prediction={getPrediction(predictions, match.id)}
                  teams={teams}
                  onClick={() => onPredict(match.id)}
                />
              ))
            ) : (
              <p className="qc-empty">No hay partidos próximos por ahora.</p>
            )}
          </div>
        </section>
        <section>
          <SectionTitle title="Último resultado" />
          <div className="qc-result-panel">
            {lastFinished ? (
              <>
                <MatchTeams match={lastFinished} teams={teams} />
                <strong>
                  {lastFinished.result.home} - {lastFinished.result.away}
                </strong>
                {formatTiebreaker(lastFinished, teams) ? <small>{formatTiebreaker(lastFinished, teams)}</small> : null}
              </>
            ) : (
              <>
                <Activity size={28} />
                <p>Los resultados confirmados aparecerán aquí cuando el admin cierre partidos.</p>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function WinnersCelebration({ ranking }) {
  const topThree = ranking.slice(0, 3);
  if (!topThree.length) return null;

  return (
    <section className="qc-winners" aria-label="Ganadores de la quiniela">
      <div className="qc-winners__header">
        <p className="qc-eyebrow">Mundial finalizado</p>
        <h2>Felicitaciones a los primeros tres lugares</h2>
      </div>
      <div className="qc-winners__grid">
        {topThree.map((user, index) => {
          const Icon = index === 0 ? Crown : Medal;
          return (
            <article className={`qc-winner-card qc-winner-card--${index + 1}`} key={user.uid || user.id}>
              <span className="qc-winner-card__place">#{index + 1}</span>
              <Icon size={22} />
              <div>
                <strong>{user.name}</strong>
                <small>
                  {podiumLabels[index]} · {user.totalPoints} pts · {user.exactScores ?? 0} exactos
                </small>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <article className="qc-stat">
      <Icon size={19} />
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </article>
  );
}

function SectionTitle({ title, action }) {
  return (
    <div className="qc-section-title">
      <h2>{title}</h2>
      {action}
    </div>
  );
}

function MatchTeams({ match, teams }) {
  const home = getTeam(teams, match.homeTeam, match.homePlaceholder);
  const away = getTeam(teams, match.awayTeam, match.awayPlaceholder);

  return (
    <div className="qc-match-teams">
      <span>
        <TeamFlag team={home} />
        {home.name}
      </span>
      <i>vs</i>
      <span>
        <TeamFlag team={away} />
        {away.name}
      </span>
    </div>
  );
}

function MatchRow({ match, prediction, teams, onClick }) {
  const status = getMatchState(match, prediction);
  const stateClass = status.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  return (
    <button className="qc-match-row" onClick={onClick} type="button">
      <span className="qc-match-row__number">#{getDisplayOrder(match)}</span>
      <div>
        <MatchTeams match={match} teams={teams} />
        <small>
          {stageLabels[match.stage]} {match.group ? `· Grupo ${match.group}` : ""} · {formatDate(match.dateTime)}
        </small>
      </div>
      {prediction ? (
        <strong className="qc-score-pill">
          {prediction.homeScore}-{prediction.awayScore}
        </strong>
      ) : null}
      <span className={`qc-status qc-status--${stateClass}`}>{status}</span>
    </button>
  );
}

function MatchesPage({ workspace, selectedMatchId, onSelectMatch, onSavePrediction }) {
  const [groupFilter, setGroupFilter] = useState("all");
  const groups = useMemo(() => ["all", ...new Set(workspace.matches.map((match) => match.group).filter(Boolean))], [workspace.matches]);
  const selectedMatch = useMemo(
    () => workspace.matches.find((match) => match.id === selectedMatchId) || workspace.matches[0],
    [selectedMatchId, workspace.matches]
  );
  const filteredMatches = useMemo(
    () => workspace.matches.filter((match) => groupFilter === "all" || match.group === groupFilter),
    [groupFilter, workspace.matches]
  );
  const bulkMatches = useMemo(() => filteredMatches.filter(isPredictableMatch), [filteredMatches]);

  return (
    <div className="qc-page qc-fade">
      <SectionTitle
        title="Partidos"
        action={
          <div className="qc-filter">
            {groups.map((group) => (
              <button
                className={groupFilter === group ? "is-active" : ""}
                key={group}
                onClick={() => setGroupFilter(group)}
                type="button"
              >
                {group === "all" ? "Todos" : group}
              </button>
            ))}
          </div>
        }
      />
      <PredictionPanel
        key={selectedMatch?.id}
        match={selectedMatch}
        prediction={getPrediction(workspace.predictions, selectedMatch?.id)}
        profile={workspace.profile}
        teams={workspace.teams}
        onSave={onSavePrediction}
      />
      <BulkPredictionsPanel
        matches={bulkMatches}
        predictions={workspace.predictions}
        profile={workspace.profile}
        teams={workspace.teams}
        onSave={onSavePrediction}
      />
      <div className="qc-list">
        {filteredMatches.map((match) => (
          <MatchRow
            key={match.id}
            match={match}
            prediction={getPrediction(workspace.predictions, match.id)}
            teams={workspace.teams}
            onClick={() => onSelectMatch(match.id)}
          />
        ))}
      </div>
    </div>
  );
}

function BulkPredictionsPanel({ matches, predictions, profile, teams, onSave }) {
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setDrafts((current) => {
      const next = {};
      matches.forEach((match) => {
        const prediction = getPrediction(predictions, match.id);
        next[match.id] = current[match.id] || {
          homeScore: prediction?.homeScore ?? 0,
          awayScore: prediction?.awayScore ?? 0
        };
      });
      return next;
    });
  }, [matches, predictions]);

  if (!matches.length) return null;

  const change = (matchId, side, delta) => {
    setMessage("");
    setDrafts((current) => {
      const currentScore = current[matchId] || { homeScore: 0, awayScore: 0 };
      const key = side === "home" ? "homeScore" : "awayScore";
      return {
        ...current,
        [matchId]: {
          ...currentScore,
          [key]: Math.max(0, Math.min(15, currentScore[key] + delta))
        }
      };
    });
  };

  const saveAll = async () => {
    setBusy(true);
    setMessage("");

    const payloads = matches.map((match) => ({
      uid: profile.uid,
      matchId: match.id,
      homeScore: drafts[match.id]?.homeScore ?? 0,
      awayScore: drafts[match.id]?.awayScore ?? 0
    }));

    const results = await Promise.allSettled(payloads.map((payload) => onSave(payload)));
    const saved = results.filter((result) => result.status === "fulfilled").length;
    const failed = results.find((result) => result.status === "rejected");

    setBusy(false);
    if (failed) {
      setMessage(`Se guardaron ${saved} de ${payloads.length}. ${failed.reason?.message || "Intenta de nuevo."}`);
      return;
    }

    setMessage(`Listo: ${saved} predicciones guardadas.`);
  };

  return (
    <section className="qc-bulk-predictions">
      <div className="qc-bulk-predictions__header">
        <div>
          <p className="qc-eyebrow">Pronóstico rápido</p>
          <h2>Guardar todos los partidos disponibles</h2>
        </div>
        <button className="qc-button qc-button--primary" disabled={busy} onClick={saveAll} type="button">
          <Save size={18} />
          {busy ? "Guardando..." : `Guardar ${matches.length}`}
        </button>
      </div>
      <div className="qc-bulk-predictions__list">
        {matches.map((match) => {
          const home = getTeam(teams, match.homeTeam, match.homePlaceholder);
          const away = getTeam(teams, match.awayTeam, match.awayPlaceholder);
          const score = drafts[match.id] || { homeScore: 0, awayScore: 0 };

          return (
            <article className="qc-bulk-match" key={match.id}>
              <div className="qc-bulk-match__info">
                <span>#{getDisplayOrder(match)}</span>
                <MatchTeams match={match} teams={teams} />
                <small>{formatDate(match.dateTime)}</small>
              </div>
              <div className="qc-bulk-match__score">
                <MiniScoreControl
                  label={home.name}
                  value={score.homeScore}
                  onMinus={() => change(match.id, "home", -1)}
                  onPlus={() => change(match.id, "home", 1)}
                  disabled={busy}
                />
                <span>:</span>
                <MiniScoreControl
                  label={away.name}
                  value={score.awayScore}
                  onMinus={() => change(match.id, "away", -1)}
                  onPlus={() => change(match.id, "away", 1)}
                  disabled={busy}
                />
              </div>
            </article>
          );
        })}
      </div>
      {message ? <p className={message.startsWith("Listo") ? "qc-success" : "qc-error"}>{message}</p> : null}
    </section>
  );
}

function MiniScoreControl({ label, value, onMinus, onPlus, disabled }) {
  return (
    <div className="qc-mini-score">
      <small>{label}</small>
      <div>
        <button aria-label={`Restar gol a ${label}`} disabled={disabled} onClick={onMinus} type="button">
          <Minus size={15} />
        </button>
        <output>{value}</output>
        <button aria-label={`Sumar gol a ${label}`} disabled={disabled} onClick={onPlus} type="button">
          <Plus size={15} />
        </button>
      </div>
    </div>
  );
}

function PredictionPanel({ match, prediction, profile, teams, onSave }) {
  const [homeScore, setHomeScore] = useState(prediction?.homeScore ?? 0);
  const [awayScore, setAwayScore] = useState(prediction?.awayScore ?? 0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  if (!match) return null;
  const closed = new Date(match.lockAt).getTime() <= Date.now() || match.status === "closed" || match.status === "finished";
  const exactPoints = 9 * match.multiplier;
  const outcomePoints = 3 * match.multiplier;
  const home = getTeam(teams, match.homeTeam, match.homePlaceholder);
  const away = getTeam(teams, match.awayTeam, match.awayPlaceholder);

  const change = (side, delta) => {
    const setter = side === "home" ? setHomeScore : setAwayScore;
    setter((value) => Math.max(0, Math.min(15, value + delta)));
  };

  const save = async () => {
    setBusy(true);
    setMessage("");
    try {
      await onSave({ uid: profile.uid, matchId: match.id, homeScore, awayScore });
      setMessage("Predicción guardada.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="qc-prediction">
      <div className="qc-prediction__meta">
        <span>Partido #{getDisplayOrder(match)}</span>
        <span>{stageLabels[match.stage]}</span>
        {match.group ? <span>Grupo {match.group}</span> : null}
      </div>
      <div className="qc-scoreboard">
        <ScoreControl team={home} value={homeScore} onMinus={() => change("home", -1)} onPlus={() => change("home", 1)} disabled={closed} />
        <span className="qc-scoreboard__divider">:</span>
        <ScoreControl team={away} value={awayScore} onMinus={() => change("away", -1)} onPlus={() => change("away", 1)} disabled={closed} />
      </div>
      <div className="qc-points-rule">
        <span>
          <strong>{outcomePoints}</strong> por ganador o empate
        </span>
        <span>
          <strong>{exactPoints}</strong> por marcador exacto
        </span>
        <span>
          <strong>x{match.multiplier}</strong> multiplicador
        </span>
      </div>
      <p className="qc-lock-rule">
        Puedes predecir y editar marcador/ganador hasta 30 minutos antes del partido.
      </p>
      <div className="qc-prediction__footer">
        <p>
          {formatDate(match.dateTime)} · Cierra {formatDate(match.lockAt)}
        </p>
        <button className="qc-button qc-button--primary" disabled={closed || busy} onClick={save} type="button">
          {closed ? <Lock size={18} /> : <Save size={18} />}
          {closed ? "Predicción cerrada" : busy ? "Guardando..." : "Guardar predicción"}
        </button>
      </div>
      {message ? <p className={message.includes("guardada") ? "qc-success" : "qc-error"}>{message}</p> : null}
    </section>
  );
}

function ScoreControl({ team, value, onMinus, onPlus, disabled }) {
  return (
    <div className="qc-score-control">
      <div className="qc-score-control__team">
        <TeamFlag team={team} />
        <strong>{team.name}</strong>
      </div>
      <div className="qc-stepper">
        <button aria-label="Restar gol" disabled={disabled} onClick={onMinus} type="button">
          <Minus size={18} />
        </button>
        <output>{value}</output>
        <button aria-label="Sumar gol" disabled={disabled} onClick={onPlus} type="button">
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

function RankingPage({ workspace }) {
  const topThree = workspace.ranking.slice(0, 3);

  return (
    <div className="qc-page qc-fade">
      <SectionTitle title="Tabla de posiciones" />
      <section className="qc-podium">
        {topThree.map((user, index) => (
          <article className={`qc-podium__place qc-podium__place--${index + 1}`} key={user.uid}>
            <Crown size={22} />
            <span>{index + 1}</span>
            <strong>{user.name}</strong>
            <small>
              {participantLabel(user.participantType)} · {user.totalPoints} pts
            </small>
          </article>
        ))}
      </section>
      <PrizeBoard ranking={workspace.ranking} />
      <RankingTable users={workspace.ranking} currentUid={workspace.profile?.uid} />
    </div>
  );
}

function PrizeBoard({ ranking = [] }) {
  return (
    <section className="qc-prize-board" aria-label="Premios del ranking">
      <div className="qc-prize-board__header">
        <p className="qc-eyebrow">Premios asignados</p>
        <h2>Ganadores de la quiniela</h2>
      </div>
      <div className="qc-prize-grid">
        {rankingPrizes.map((prize) => {
          const Icon = prize.icon;
          const winners = getPrizeWinners(ranking, prize);
          return (
            <article className="qc-prize-card" key={prize.place}>
              <span className="qc-prize-card__icon">
                <Icon size={22} />
              </span>
              <div>
                <strong>{prize.place}</strong>
                <p>{prize.description}</p>
              </div>
              <small>{prize.quantity}</small>
              <div className="qc-prize-winners" aria-label={`Ganadores de ${prize.place}`}>
                {winners.length ? (
                  winners.map((winner, index) => (
                    <span className="qc-prize-winner" key={winner.uid || winner.id || `${prize.place}-${index}`}>
                      <b>#{prize.from + index}</b>
                      {winner.name}
                    </span>
                  ))
                ) : (
                  <span className="qc-prize-winner qc-prize-winner--empty">Por definir</span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RulesSection() {
  const examples = [
    { prediction: "2-1", result: "3-0", label: "Aciertas ganador", points: "3 pts" },
    { prediction: "2-1", result: "2-1", label: "Marcador exacto", points: "9 pts" },
    { prediction: "1-1", result: "2-2", label: "Aciertas empate", points: "3 pts" },
    { prediction: "1-0", result: "0-1", label: "No aciertas desenlace", points: "0 pts" }
  ];

  return (
    <section className="qc-rules-page">
      <SectionTitle title="Reglas" />
      <section className="qc-rules-hero">
        <p className="qc-eyebrow">Reglas de Pulso Mundialista</p>
        <h1>Así se gana cada punto.</h1>
        <p>
          Tu predicción incluye marcador y ganador. Puedes crearla o editarla hasta 30 minutos antes de que empiece el partido.
        </p>
      </section>

      <section className="qc-rules-grid" aria-label="Reglas principales">
        <article className="qc-rule-card qc-rule-card--primary">
          <span>01</span>
          <h2>Marcador exacto</h2>
          <strong>9 puntos</strong>
          <p>Si tu marcador es idéntico al resultado final, recibes 9 puntos total.</p>
        </article>
        <article className="qc-rule-card">
          <span>02</span>
          <h2>Ganador o empate</h2>
          <strong>3 puntos</strong>
          <p>Si aciertas el desenlace pero no el marcador exacto, recibes 3 puntos.</p>
        </article>
        <article className="qc-rule-card">
          <span>03</span>
          <h2>Sin acumulación</h2>
          <strong>Máximo aplicable</strong>
          <p>El marcador exacto vale 9 total. No suma 3 + 9.</p>
        </article>
      </section>

      <section className="qc-multiplier-panel">
        <div>
          <p className="qc-eyebrow">Multiplicador</p>
          <h2>Todos los partidos valen x1</h2>
          <p>
            <code>x1</code> significa que todos los partidos usan los puntos base: 3 por ganador/empate y 9 por marcador exacto.
          </p>
        </div>
        <div className="qc-multiplier-table" aria-label="Ejemplos de multiplicador">
          <span>x1</span>
          <strong>3 / 9 pts</strong>
        </div>
      </section>

      <section className="qc-example-grid" aria-label="Ejemplos de puntuación">
        {examples.map((example) => (
          <article className="qc-example-card" key={`${example.prediction}-${example.result}`}>
            <div>
              <span>Predicción</span>
              <strong>{example.prediction}</strong>
            </div>
            <div>
              <span>Resultado</span>
              <strong>{example.result}</strong>
            </div>
            <p>{example.label}</p>
            <b>{example.points}</b>
          </article>
        ))}
      </section>
    </section>
  );
}

function RankingTable({ users, currentUid, offset = 1 }) {
  return (
    <div className="qc-table-wrap">
      <table className="qc-table">
        <thead>
          <tr>
            <th>Posición</th>
            <th>Participante</th>
            <th>Puntos</th>
            <th>Aciertos</th>
            <th>Exactos</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr className={user.uid === currentUid ? "is-current" : ""} key={user.uid}>
              <td>#{index + offset}</td>
              <td>
                <span className="qc-avatar">{initials(user.name)}</span>
                <span className="qc-rank-person">
                  <strong>{user.name}</strong>
                  <small>{participantLabel(user.participantType)}</small>
                </span>
              </td>
              <td>{user.totalPoints}</td>
              <td>{user.correctOutcomes}</td>
              <td>{user.exactScores}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfilePage({ workspace }) {
  const { profile, predictions, matches, teams } = workspace;
  const completedPredictions = predictions
    .map((prediction) => ({
      prediction,
      match: matches.find((match) => match.id === prediction.matchId)
    }))
    .filter((item) => item.match);

  return (
    <div className="qc-page qc-fade">
      <section className="qc-profile-hero">
        <span className="qc-profile-hero__avatar">{initials(profile?.name)}</span>
        <h1>{profile?.name}</h1>
        <p>{profile?.email}</p>
        <div>
          <StatPill label="Tipo" value={participantLabel(profile?.participantType)} />
          <StatPill label="Puntos" value={profile?.totalPoints ?? 0} />
          <StatPill label="Aciertos" value={profile?.correctOutcomes ?? 0} />
          <StatPill label="Exactos" value={profile?.exactScores ?? 0} />
          <StatPill
            label={profile?.participantType === "medico" ? "Colegiado" : "Código"}
            value={profile?.participantType === "medico" ? profile?.professionalLicense || "-" : profile?.employeeCode || "-"}
          />
        </div>
      </section>

      <RulesSection />

      <SectionTitle title="Logros" />
      <div className="qc-badges">
        <Badge icon={Check} label="Primer pronóstico" active={profile?.predictionsCount > 0} />
        <Badge icon={Medal} label="Marcador exacto" active={profile?.exactScores > 0} />
        <Badge icon={Trophy} label="Doble dígito" active={profile?.totalPoints >= 10} />
      </div>

      <SectionTitle title="Historial" />
      <div className="qc-list">
        {completedPredictions.length ? (
          completedPredictions.map(({ prediction, match }) => (
            <MatchRow key={prediction.matchId} match={match} prediction={prediction} teams={teams} onClick={() => {}} />
          ))
        ) : (
          <p className="qc-empty">Aún no tienes predicciones guardadas.</p>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <span className="qc-stat-pill">
      <strong>{value}</strong>
      {label}
    </span>
  );
}

function Badge({ icon: Icon, label, active }) {
  return (
    <article className={active ? "qc-badge is-active" : "qc-badge"}>
      <Icon size={20} />
      <span>{label}</span>
    </article>
  );
}

function AdminPage({ workspace, onImport, onUpdateMatch, onSaveResult }) {
  const [importText, setImportText] = useState("correo,password,nombre,tipo\nnuevo.colaborador@centromedico.com.gt,quiniela2026,Nuevo Colaborador,colaborador\nana.lopez@centromedico.com.gt,quiniela2026,Dra. Ana López,medico");
  const [importResult, setImportResult] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState(workspace.matches[0]?.id || seedMatches[0].id);
  const selectedMatch = workspace.matches.find((match) => match.id === selectedMatchId) || workspace.matches[0];
  const activeUsers = workspace.ranking.filter((user) => user.predictionsCount > 0).length;
  const totalPredictions = workspace.ranking.reduce((sum, user) => sum + (user.predictionsCount || 0), 0);

  const submitImport = async () => {
    const parsed = parseBulkUsers(importText);
    try {
      const result = await onImport(parsed.rows);
      setImportResult({ ...result, invalid: parsed.invalid.length, duplicatesInPaste: parsed.duplicatesInPaste });
    } catch (error) {
      setImportResult({ imported: 0, duplicates: 0, invalid: parsed.invalid.length, error: error.message });
    }
  };

  const exportCsv = (type) => {
    const { rankingCsv, authorizedCsv } = buildCsvExports(workspace);
    downloadCsv(type === "ranking" ? "ranking-quiniela-cm.csv" : "usuarios-autorizados-cm.csv", type === "ranking" ? rankingCsv : authorizedCsv);
  };

  return (
    <div className="qc-page qc-page--admin qc-fade">
      <SectionTitle title="Panel de administración" />
      <section className="qc-stat-grid">
        <StatCard icon={Users} label="Registrados" value={workspace.ranking.length} sub="Cuentas activas" />
        <StatCard icon={ClipboardList} label="Predicciones" value={totalPredictions} sub="Guardadas" />
        <StatCard icon={Activity} label="Activos" value={`${workspace.ranking.length ? Math.round((activeUsers / workspace.ranking.length) * 100) : 0}%`} sub="Con predicciones" />
        <StatCard icon={CalendarDays} label="Partidos" value={workspace.matches.length} sub="Calendario cargado" />
      </section>

      <div className="qc-admin-grid">
        <section className="qc-admin-panel">
          <h2>Importar usuarios</h2>
          <p className="qc-admin-help">Formato: correo, password, nombre, tipo. Tipo puede ser medico o colaborador.</p>
          <textarea onChange={(event) => setImportText(event.target.value)} rows={9} value={importText} />
          <button className="qc-button qc-button--primary" onClick={submitImport} type="button">
            <Upload size={18} />
            Importar usuarios
          </button>
          {importResult ? (
            <p className={importResult.error ? "qc-error" : "qc-success"}>
              {importResult.error || `${importResult.imported} usuarios creados · ${importResult.duplicates} duplicados existentes · ${importResult.invalid} inválidos`}
            </p>
          ) : null}
        </section>

        <section className="qc-admin-panel">
          <h2>Exportar datos</h2>
          <div className="qc-admin-actions">
            <button className="qc-button" onClick={() => exportCsv("ranking")} type="button">
              <Download size={18} />
              Ranking CSV
            </button>
            <button className="qc-button" onClick={() => exportCsv("authorized")} type="button">
              <ArrowDownToLine size={18} />
              Autorizados CSV
            </button>
          </div>
          <div className="qc-authorized-list">
            {workspace.authorizedEmails.slice(0, 6).map((item) => (
              <span key={item.email}>
                {item.name} · {participantLabel(item.participantType)} · {item.used ? "activo" : "pendiente"}
              </span>
            ))}
          </div>
        </section>
      </div>

      <section className="qc-admin-panel qc-admin-panel--wide">
        <h2>Gestión de partidos</h2>
        <div className="qc-admin-match-editor">
          <select onChange={(event) => setSelectedMatchId(event.target.value)} value={selectedMatchId}>
            {workspace.matches.map((match) => (
              <option key={match.id} value={match.id}>
                #{getDisplayOrder(match)} · {formatDate(match.dateTime)} · {stageLabels[match.stage]} · {match.venue}
              </option>
            ))}
          </select>
          {selectedMatch ? (
            <AdminMatchForm
              key={selectedMatch.id}
              match={selectedMatch}
              teams={workspace.teams}
              onUpdateMatch={onUpdateMatch}
              onSaveResult={onSaveResult}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}

function AdminMatchForm({ match, teams, onUpdateMatch, onSaveResult }) {
  const [status, setStatus] = useState(match.status);
  const [dateTime, setDateTime] = useState(toDateTimeLocalValue(match.dateTime));
  const [home, setHome] = useState(match.result?.home ?? 0);
  const [away, setAway] = useState(match.result?.away ?? 0);
  const [winnerTeam, setWinnerTeam] = useState(match.winnerTeam || "");
  const [penaltyHome, setPenaltyHome] = useState(match.tiebreaker?.home ?? "");
  const [penaltyAway, setPenaltyAway] = useState(match.tiebreaker?.away ?? "");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [busy, setBusy] = useState(false);
  const tiedKnockout = isKnockoutMatch(match) && home !== "" && away !== "" && Number(home) === Number(away);
  const homeTeam = getTeam(teams, match.homeTeam, match.homePlaceholder);
  const awayTeam = getTeam(teams, match.awayTeam, match.awayPlaceholder);

  useEffect(() => {
    setStatus(match.status);
    setDateTime(toDateTimeLocalValue(match.dateTime));
    setHome(match.result?.home ?? 0);
    setAway(match.result?.away ?? 0);
    setWinnerTeam(match.winnerTeam || "");
    setPenaltyHome(match.tiebreaker?.home ?? "");
    setPenaltyAway(match.tiebreaker?.away ?? "");
    setMessage("");
    setMessageType("success");
  }, [
    match.id,
    match.status,
    match.dateTime,
    match.result?.home,
    match.result?.away,
    match.winnerTeam,
    match.tiebreaker?.home,
    match.tiebreaker?.away
  ]);

  const showError = (error, fallback) => {
    setMessageType("error");
    setMessage(error.message || fallback);
  };

  const resultScore = (value) => {
    if (value === "") throw new Error("Ingresa ambos marcadores.");
    const score = Number(value);
    if (!Number.isInteger(score) || score < 0 || score > 50) {
      throw new Error("Usa marcadores enteros entre 0 y 50.");
    }
    return score;
  };

  const update = async () => {
    setBusy(true);
    setMessage("");
    try {
      const nextDate = dateTimeLocalValueToDate(dateTime);
      if (!nextDate) throw new Error("Revisa la fecha y hora del partido.");

      await onUpdateMatch(match.id, {
        status,
        multiplier: 1,
        dateTime: nextDate.toISOString(),
        lockAt: new Date(nextDate.getTime() - 30 * 60 * 1000).toISOString()
      });
      setMessageType("success");
      setMessage("Partido actualizado.");
    } catch (error) {
      showError(error, "No se pudo actualizar el partido.");
    } finally {
      setBusy(false);
    }
  };

  const saveResult = async () => {
    setBusy(true);
    setMessage("");
    try {
      const homeScore = resultScore(home);
      const awayScore = resultScore(away);
      const resultPayload = {
        matchId: match.id,
        home: homeScore,
        away: awayScore
      };

      if (isKnockoutMatch(match) && homeScore === awayScore) {
        resultPayload.winnerTeam = winnerTeam;
        resultPayload.tiebreaker = {
          type: "penalties",
          home: resultScore(penaltyHome),
          away: resultScore(penaltyAway)
        };
      }

      setMessageType("success");
      setMessage("Guardando resultado...");
      await onSaveResult(resultPayload, (progress) => {
        if (progress?.message) {
          setMessageType("success");
          setMessage(progress.message);
        }
      });
      setStatus("finished");
      setHome(homeScore);
      setAway(awayScore);
      if (resultPayload.winnerTeam) {
        setWinnerTeam(resultPayload.winnerTeam);
        setPenaltyHome(resultPayload.tiebreaker.home);
        setPenaltyAway(resultPayload.tiebreaker.away);
      }
      setMessageType("success");
      setMessage("Resultado confirmado y puntos recalculados.");
    } catch (error) {
      showError(error, "No se pudo guardar el resultado.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="qc-admin-match-form">
      <MatchTeams match={match} teams={teams} />
      <label>
        Fecha y hora
        <input onChange={(event) => setDateTime(event.target.value)} type="datetime-local" value={dateTime} />
      </label>
      <label>
        Estado
        <select onChange={(event) => setStatus(event.target.value)} value={status}>
          <option value="upcoming">Próximo</option>
          <option value="open">Abierto</option>
          <option value="closed">Cerrado</option>
          <option value="finished">Finalizado</option>
        </select>
      </label>
      <label>
        Multiplicador
        <input disabled min="1" step="1" type="number" value={1} />
      </label>
      <div className="qc-result-inputs">
        <input min="0" onChange={(event) => setHome(event.target.value)} type="number" value={home} />
        <span>-</span>
        <input min="0" onChange={(event) => setAway(event.target.value)} type="number" value={away} />
      </div>
      {tiedKnockout ? (
        <div className="qc-tiebreaker-panel">
          <label>
            Ganador por penales
            <select onChange={(event) => setWinnerTeam(event.target.value)} value={winnerTeam}>
              <option value="">Selecciona ganador</option>
              <option value={match.homeTeam}>{homeTeam.name}</option>
              <option value={match.awayTeam}>{awayTeam.name}</option>
            </select>
          </label>
          <div className="qc-result-inputs">
            <input aria-label={`Penales de ${homeTeam.name}`} min="0" onChange={(event) => setPenaltyHome(event.target.value)} type="number" value={penaltyHome} />
            <span>-</span>
            <input aria-label={`Penales de ${awayTeam.name}`} min="0" onChange={(event) => setPenaltyAway(event.target.value)} type="number" value={penaltyAway} />
          </div>
        </div>
      ) : null}
      <div className="qc-admin-actions">
        <button className="qc-button" disabled={busy} onClick={update} type="button">
          <Save size={18} />
          {busy ? "Guardando..." : "Guardar ajustes"}
        </button>
        <button className="qc-button qc-button--primary" disabled={busy} onClick={saveResult} type="button">
          <Check size={18} />
          {busy ? "Guardando..." : "Confirmar resultado"}
        </button>
      </div>
      {message ? <p className={messageType === "error" ? "qc-error" : "qc-success"}>{message}</p> : null}
    </div>
  );
}

export default function App() {
  const [authUser, setAuthUser] = useState(null);
  const [workspace, setWorkspace] = useState(emptyWorkspace);
  const [view, setView] = useState("dashboard");
  const [selectedMatchId, setSelectedMatchId] = useState("match-001");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => store.onAuthStateChanged(setAuthUser), []);

  useEffect(() => {
    if (!authUser?.uid) {
      setWorkspace(emptyWorkspace);
      return undefined;
    }
    return store.subscribeWorkspace(authUser.uid, setWorkspace);
  }, [authUser]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [view]);

  const sortedWorkspace = useMemo(
    () => ({
      ...workspace,
      matches: withDisplayOrder(workspace.matches),
      ranking: rankUsers(workspace.ranking)
    }),
    [workspace]
  );

  const handleAuth = async (action, ...args) => {
    setAuthBusy(true);
    setAuthError("");
    try {
      await action(...args);
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  };

  const navigateToPrediction = (matchId) => {
    setSelectedMatchId(matchId);
    setView("matches");
  };

  if (!authUser) {
    return (
      <div className="qc-app">
        <LoginScreen
          busy={authBusy}
          error={authError}
          onLogin={(email, password) => handleAuth(store.loginUser, email, password)}
          onPasswordReset={(email) => store.sendPasswordReset(email)}
          onRegister={(payload) => handleAuth(store.registerUser, payload)}
        />
      </div>
    );
  }

  const profile = sortedWorkspace.profile;

  return (
    <div className="qc-app">
      <AppShell currentView={view} onLogout={store.logoutUser} onNavigate={setView} profile={profile}>
        <DemoBanner onReset={() => store.resetDemoData?.()} />
        {view === "dashboard" ? <Dashboard workspace={sortedWorkspace} onPredict={navigateToPrediction} /> : null}
        {view === "matches" ? (
          <MatchesPage
            onSavePrediction={store.savePrediction}
            onSelectMatch={setSelectedMatchId}
            selectedMatchId={selectedMatchId}
            workspace={sortedWorkspace}
          />
        ) : null}
        {view === "ranking" ? <RankingPage workspace={sortedWorkspace} /> : null}
        {view === "profile" ? <ProfilePage workspace={sortedWorkspace} /> : null}
        {view === "admin" && profile?.role === "admin" ? (
          <AdminPage
            onImport={store.importUsers || store.importAuthorizedEmails}
            onSaveResult={store.saveMatchResult}
            onUpdateMatch={store.updateMatch}
            workspace={sortedWorkspace}
          />
        ) : null}
      </AppShell>
    </div>
  );
}
